import {css, StyleSheet} from 'aphrodite';
import classNames from 'classnames';
import React from 'react';

import {assert, assertAndContinue, assertEqual} from 'Asserts';
import BenchlingConfig from 'BenchlingConfig';
import ModelTypeConstants from 'constants/ModelTypeConstants';
import {StorableItemType} from 'constants/ModelTypeConstants';
import {
  ChildContainerStatus,
  LocationCapacityInfo,
  MODAL_PANE,
  ParentStorableData,
  SelectedParentStorable,
  StorableSummary,
  StorableSummaryExtras,
} from 'constants/SampleConstants';
import {
  areItemsValidForParentStorable,
  getCapacityErrorMessage,
  ParentStorableWithCapacityInfo,
  PotentialChildStorable,
} from 'inventory/location-validation/ParentStorableValidationUtils';
import {compareCoordinatesRowMajor} from 'samples/grids/GridCellTableSort';
import {getTotalGridCapacity} from 'samples/grids/GridUtil';
import {
  getAvailableCapacity,
  getAvailableSpaceDisplayString,
  isFixedPlate,
  isGridId,
  isGridSummary,
  isMatrixPlateOrBox,
} from 'samples/SampleUtil';
import BenchlingIcon from 'ui-library/icons/BenchlingIcon';
import {getTooltipTitleForSearchResult} from 'ui-library/inputs/ItemOptionContent';
import {TreeItem} from 'ui-library/select-tree/SelectTreeConstants';
import APIUtil, {apiIdToIconName} from 'util/APIUtil';

import {StorableSummaryGraphQL} from '../../../graphql-fragments/inventory/__generated/StorableSummaryGraphQL';

/**
 * Returns parentStorableData from SELECT_PARENT pane state.
 */
export function getParentStorableData({storable, gridCells}: SelectedParentStorable): ParentStorableData {
  assert(gridCells.length <= 1, 'Expected to select 0 or 1 grid cells.');
  return {
    id: storable ? storable.id : null,
    gridCellRowIndex: gridCells.length ? gridCells[0].rowIndex : null,
    gridCellColumnIndex: gridCells.length ? gridCells[0].columnIndex : null,
  };
}

/**
 * Returns array of parentStorableData from SELECT_PARENT pane state.
 */
export function getParentStorableDatas(
  numToCreate: number,
  {storable, gridCells}: SelectedParentStorable
): Array<ParentStorableData> {
  if (storable && isGridId(storable.id)) {
    assertEqual(numToCreate, gridCells.length, `Expected ${numToCreate} grid cells to be selected.`);
    const sortedGridCells = [...gridCells];
    // Shockingly, javascript sorts numbers alphabetically, so we have to implement the comparison ourselves
    sortedGridCells.sort(compareCoordinatesRowMajor);
    return sortedGridCells.map((gridCell) => getParentStorableData({storable, gridCells: [gridCell]}));
  }
  return Array(numToCreate).fill(getParentStorableData({storable, gridCells: []}));
}

export function isParentStorableRequired(modalPaneKey: keyof typeof MODAL_PANE): boolean {
  return modalPaneKey !== MODAL_PANE.CREATE_LOCATION && BenchlingConfig.ENABLE_REQUIRED_PARENT_STORABLE;
}

export function getValidMoveDestinationStorableTypes(storableSchema: {id: string}): Array<StorableItemType> {
  const storableSchemaClass = APIUtil.apiIdToType(storableSchema.id);
  if (storableSchemaClass === ModelTypeConstants.CONTAINER_SCHEMA) {
    return [ModelTypeConstants.BOX, ModelTypeConstants.MATRIX_PLATE, ModelTypeConstants.LOCATION];
  } else {
    return [ModelTypeConstants.LOCATION];
  }
}

export function getEmptySelectedParent(): SelectedParentStorable {
  return {storable: null, gridCells: []};
}

// Keep in sync with benchling/inventory/storables/ancestors.py:get_ancestral_storables_path.
export function getAncestralStorablesDisplayString(ancestralStorables: Array<StorableSummary>): string {
  // `ancestralStorables` includes the current item as the first element in the list, so first remove it.
  // The list also needs to be reversed since we want to display them in top-down order.
  if (ancestralStorables.length === 1) {
    return 'No location';
  }
  return ancestralStorables
    .slice(1)
    .reverse()
    .map(({name}) => name)
    .join(' / ');
}

export function getParentStorableFromAncestralStorables(
  storable: StorableSummary,
  ancestralStorables: Array<StorableSummaryGraphQL>
): StorableSummaryGraphQL | null {
  assertEqual(
    storable.id,
    ancestralStorables[0].id,
    'Expected storable to be the first object in ancestralStorables'
  );
  if (ancestralStorables.length === 1) {
    // Either the storable does not have a parent, or the parent is not accessible.
    return null;
  }
  const parentStorable = ancestralStorables[1];
  if (storable.parentStorableId !== parentStorable.id) {
    // The parent is likely not accessible, and it was filtered out of ancestralStorables.
    return null;
  }
  return parentStorable;
}

export function getParentCapacityStatusDisplayString(
  storable: StorableSummary,
  capacityInfo: ChildContainerStatus | LocationCapacityInfo,
  notEnoughOpenPositions: boolean = false
): string | null {
  assert(
    isGridId(storable.id) || APIUtil.isLocation(storable.id),
    'Expected storable to be a plate, box or location.'
  );
  if (isFixedPlate(storable)) {
    const {numFilledContainers, numTotalContainers} = capacityInfo as ChildContainerStatus;
    return `${numTotalContainers - numFilledContainers} of ${numTotalContainers} wells empty`;
  } else {
    const totalAndAvailableCapacity = getTotalAndAvailableCapacityForStorable(storable, capacityInfo);
    if (totalAndAvailableCapacity == null) {
      return null;
    }
    const {totalCapacity, availableCapacity} = totalAndAvailableCapacity;
    const positionCountString = `${availableCapacity} of ${totalCapacity} positions open`;
    if (notEnoughOpenPositions) {
      return `${positionCountString}: not enough open positions`;
    } else {
      return positionCountString;
    }
  }
}

/**
 * Returns whether storables are valid to be moved into the parent destination.
 * @param destinationStorable potential parent to move items into
 * @param storablesToBeMoved storables being moved
 * @param extras storable summary extras, with capacity info for the parents
 *
 * @returns whether the move can occur (isValid) plus some additional information about the end state -- an
 * error message if invalid, and hasCapacityError to indicate if the error is due to the parent ending up
 * over capacity (used for styling of the capacity information). Error message is the one that takes
 * precedence (allows children > schema > capacity error).
 *
 */
export function storablesCanBeMovedIntoParent({
  destinationStorable,
  storablesToBeMoved,
  extras,
}: {
  destinationStorable: StorableSummary;
  storablesToBeMoved?: Array<PotentialChildStorable> | null;
  extras?: StorableSummaryExtras;
}): {
  isValid: boolean;
  errorMessage: string | null;
  hasDisallowedOrInvalidSchemaChildren: boolean;
} {
  const numStorablesToBeMoved = storablesToBeMoved?.length ?? 0;
  if (numStorablesToBeMoved === 0) {
    return {
      isValid: true,
      errorMessage: null,
      hasDisallowedOrInvalidSchemaChildren: false,
    };
  }
  if (isFixedPlate(destinationStorable)) {
    return {
      isValid: false,
      errorMessage: null,
      hasDisallowedOrInvalidSchemaChildren: false,
    };
  }
  // This shouldn't happen, but if it does it means storablesToBeMoved was incorrectly passed through in
  // a place where containers are included in the options.
  if (!APIUtil.isLocation(destinationStorable.id) && !isGridSummary(destinationStorable)) {
    assertAndContinue(
      false,
      `storablesCanBeMovedIntoParent called with destinationStorable that is not a location, box or plate: id=${destinationStorable.id}`
    );
    return {
      isValid: true,
      errorMessage: null,
      hasDisallowedOrInvalidSchemaChildren: false,
    };
  }

  const validityResp = validateItemsForParentStorable(destinationStorable, extras, storablesToBeMoved);
  if (validityResp == null) {
    return {
      isValid: true,
      errorMessage: null,
      hasDisallowedOrInvalidSchemaChildren: false,
    };
  }
  return validityResp;
}

function validateItemsForParentStorable(
  storable: StorableSummary,
  extras?: StorableSummaryExtras,
  storablesToBeMoved?: Array<PotentialChildStorable> | null
): {
  isValid: boolean;
  hasCapacityError: boolean;
  errorMessage: string | null;
  // String descriptor of available space (e.g. 5 / 10). Null if occupied or total capacity is not defined
  availableSpaceLabel: string | null;
  hasDisallowedOrInvalidSchemaChildren: boolean;
} | null {
  const capacityInfo = getCapacityInfoFromExtras(storable, extras);
  if (capacityInfo == null || storablesToBeMoved == null) {
    return null;
  }
  const availableSpaceLabel =
    capacityInfo.totalCapacity != null && capacityInfo.occupiedCapacity != null
      ? getAvailableSpaceDisplayString(capacityInfo.totalCapacity, capacityInfo.occupiedCapacity)
      : null;
  const errorInfo = areItemsValidForParentStorable(capacityInfo, storablesToBeMoved);
  const errorMessage = getCapacityErrorMessage(storable.name, errorInfo, true);
  const fullErrorMessage =
    errorMessage != null
      ? `${getTooltipTitleForSearchResult({name: storable.name, item: storable})}: ${errorMessage}`
      : null;
  return {
    isValid: errorInfo.isValid,
    errorMessage: fullErrorMessage,
    hasCapacityError: errorInfo.isOvercapacity,
    availableSpaceLabel,
    hasDisallowedOrInvalidSchemaChildren: Boolean(
      errorInfo.hasDisallowedChildren || errorInfo.hasInvalidSchemaChildren
    ),
  };
}

function getCapacityInfoForStorable(
  storable: StorableSummary,
  extra?: LocationCapacityInfo | ChildContainerStatus
): ParentStorableWithCapacityInfo | null {
  const locationCapacity = extra as LocationCapacityInfo;
  if (
    APIUtil.isLocation(storable.id) &&
    locationCapacity != null &&
    locationCapacity.allowsNonLocationChildren != null
  ) {
    return {
      __typename: 'LocationNode',
      totalCapacity: locationCapacity.totalCapacity,
      occupiedCapacity: locationCapacity.occupiedCapacity,
      allowsNonLocationChildren: locationCapacity.allowsNonLocationChildren,
      allowedChildStorableSchemaApiIds: locationCapacity.validChildSchemaIds,
      id: storable.id,
      name: storable.name,
    };
  }

  const childContainerStatus = extra as ChildContainerStatus;
  if (
    isMatrixPlateOrBox(storable) &&
    childContainerStatus != null &&
    childContainerStatus.numTotalContainers != null
  ) {
    const totalCapacity = getTotalGridCapacity(storable);
    return {
      __typename: APIUtil.isBox(storable.id) ? 'BoxNode' : 'PlateNode',
      totalCapacity,
      occupiedCapacity: childContainerStatus.numTotalContainers,
      id: storable.id,
      name: storable.name,
    };
  }
  return null;
}

function getCapacityInfoFromExtras(
  storable: StorableSummary,
  extras?: StorableSummaryExtras
): ParentStorableWithCapacityInfo | null {
  const capacityExtra =
    extras?.locationCapacityById?.[storable.id] ?? extras?.childContainerStatusesByGridId?.[storable.id];
  return getCapacityInfoForStorable(storable, capacityExtra);
}

function getTotalAndAvailableCapacityForStorable(
  storable: StorableSummary,
  extra?: LocationCapacityInfo | ChildContainerStatus
): {totalCapacity: number; availableCapacity: number} | null {
  const capacityInfo = getCapacityInfoForStorable(storable, extra);
  if (capacityInfo?.totalCapacity == null || capacityInfo?.occupiedCapacity == null) {
    return null;
  }
  return {
    totalCapacity: capacityInfo.totalCapacity,
    availableCapacity: getAvailableCapacity(capacityInfo.totalCapacity, capacityInfo.occupiedCapacity),
  };
}

function getAvailableSpaceInfoForSelectTreeItem({
  storable,
  storablesToBeMoved,
  extras,
  deprioritizeLocations,
}: {
  storable: StorableSummary;
  storablesToBeMoved?: Array<PotentialChildStorable> | null;
  extras?: StorableSummaryExtras;
  deprioritizeLocations: boolean;
}): {disabled: boolean; note: JSX.Element | null; errorTooltip?: string | null} {
  if (APIUtil.isLocation(storable.id) && !deprioritizeLocations) {
    return {disabled: false, note: null};
  }
  const validityResp = validateItemsForParentStorable(storable, extras, storablesToBeMoved);
  if (validityResp == null) {
    return {disabled: false, note: null};
  }
  const {isValid, availableSpaceLabel, errorMessage, hasCapacityError, hasDisallowedOrInvalidSchemaChildren} =
    validityResp;

  const numStorablesToBeMoved = storablesToBeMoved?.length ?? 0;
  const note =
    numStorablesToBeMoved > 0 && !hasDisallowedOrInvalidSchemaChildren ? (
      <>
        {hasCapacityError && <BenchlingIcon icon="ban" style={{marginRight: '8px'}} />}
        {availableSpaceLabel != null && (
          <span className={classNames(css(styles.tabularNumeric))}>{availableSpaceLabel}</span>
        )}
      </>
    ) : null;
  return {disabled: !isValid, note, errorTooltip: errorMessage};
}

export function storableToSelectTreeItem({
  storable,
  storablesToBeMoved,
  extras,
  isInitiallyExpanded,
  parentStorableTypes,
}: {
  storable: StorableSummary;
  storablesToBeMoved?: Array<PotentialChildStorable> | null;
  extras?: StorableSummaryExtras;
  isInitiallyExpanded?: boolean;
  parentStorableTypes?: Array<StorableItemType>;
}): TreeItem {
  // Only deprioritize locations if they are among the valid parent storable types. Otherwise, they are just
  // rendered so you can drill into them to find plates/boxes, and do not need to be disabled if invalid.
  const deprioritizeLocations = parentStorableTypes?.includes(ModelTypeConstants.LOCATION) ?? true;
  const {disabled, note, errorTooltip} = getAvailableSpaceInfoForSelectTreeItem({
    storable,
    storablesToBeMoved,
    extras,
    deprioritizeLocations,
  });
  return {
    forceToTop: storable.starred,
    icon: storable.starred ? 'star' : apiIdToIconName(storable.id),
    id: storable.id,
    name: storable.name,
    parentId: storable.parentStorableId || null,
    isInitiallyExpanded,
    disabled,
    secondaryLabels: note ? [note] : null,
    tooltipText: errorTooltip,
  };
}

export const styles = StyleSheet.create({
  tabularNumeric: {
    fontVariantNumeric: 'tabular-nums',
  },
});
